import { supabase } from '../lib/supabaseClient';

/**
 * Storage Service
 * Handles file uploads and downloads using Supabase Storage
 */

const RESUMES_BUCKET = 'resumes';
const LOGOS_BUCKET = 'logos';

export interface UploadResult {
  success: boolean;
  url?: string;
  path?: string;
  error?: string;
}

// Upload resume file
export const uploadResume = async (
  file: File,
  entityId: string,
  jobId: string,
  applicationId: string
): Promise<UploadResult> => {
  try {
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: 'Invalid file type. Only PDF, DOC, and DOCX files are allowed.',
      };
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'File size exceeds 5MB limit.',
      };
    }

    // Generate file path
    const fileExt = file.name.split('.').pop();
    const fileName = `${applicationId}.${fileExt}`;
    const filePath = `${entityId}/${jobId}/${fileName}`;

    // Upload file
    const { data, error } = await supabase.storage
      .from(RESUMES_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true, // Replace if exists
      });

    if (error) {
      console.error('Error uploading resume:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload file',
      };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(RESUMES_BUCKET)
      .getPublicUrl(filePath);

    return {
      success: true,
      path: data.path,
      url: urlData.publicUrl,
    };
  } catch (error) {
    console.error('Error in uploadResume:', error);
    return {
      success: false,
      error: 'An unexpected error occurred',
    };
  }
};

// Upload logo file
export const uploadLogo = async (
  file: File,
  entityId: string
): Promise<UploadResult> => {
  try {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: 'Invalid file type. Only PNG, JPG, and SVG files are allowed.',
      };
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'File size exceeds 2MB limit.',
      };
    }

    // Generate file path
    const fileExt = file.name.split('.').pop();
    const fileName = `logo.${fileExt}`;
    const filePath = `${entityId}/${fileName}`;

    // Upload file
    const { data, error } = await supabase.storage
      .from(LOGOS_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true, // Replace if exists
      });

    if (error) {
      console.error('Error uploading logo:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload file',
      };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(LOGOS_BUCKET)
      .getPublicUrl(filePath);

    return {
      success: true,
      path: data.path,
      url: urlData.publicUrl,
    };
  } catch (error) {
    console.error('Error in uploadLogo:', error);
    return {
      success: false,
      error: 'An unexpected error occurred',
    };
  }
};

// Download file
export const downloadFile = async (
  bucket: string,
  path: string
): Promise<Blob | null> => {
  try {
    const { data, error } = await supabase.storage.from(bucket).download(path);

    if (error) {
      console.error('Error downloading file:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in downloadFile:', error);
    return null;
  }
};

// Delete file
export const deleteFile = async (
  bucket: string,
  path: string
): Promise<boolean> => {
  try {
    const { error } = await supabase.storage.from(bucket).remove([path]);

    if (error) {
      console.error('Error deleting file:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteFile:', error);
    return false;
  }
};

// Get signed URL for private file (expires in 1 hour)
export const getSignedUrl = async (
  bucket: string,
  path: string,
  expiresIn: number = 3600
): Promise<string | null> => {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);

    if (error) {
      console.error('Error creating signed URL:', error);
      return null;
    }

    return data.signedUrl;
  } catch (error) {
    console.error('Error in getSignedUrl:', error);
    return null;
  }
};

// List files in a directory
export const listFiles = async (
  bucket: string,
  path: string
): Promise<any[]> => {
  try {
    const { data, error } = await supabase.storage.from(bucket).list(path);

    if (error) {
      console.error('Error listing files:', error);
      return [];
    }

    return data;
  } catch (error) {
    console.error('Error in listFiles:', error);
    return [];
  }
};

// Helper: Convert File to base64 (for preview)
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

// Get resume URL - handles both full URLs and storage paths
export const getResumeUrl = async (
  resumeUrl: string
): Promise<string | null> => {
  try {
    // Handle null/undefined/empty
    if (!resumeUrl) {
      console.warn('getResumeUrl: No resume URL provided');
      return null;
    }

    console.log('getResumeUrl: Processing URL:', resumeUrl);

    // If it's already a full URL (http/https), return as-is
    if (resumeUrl.startsWith('http://') || resumeUrl.startsWith('https://')) {
      return resumeUrl;
    }

    // If it's a placeholder from bulk upload, return null
    if (resumeUrl.startsWith('bulk-upload/')) {
      console.warn('getResumeUrl: Bulk upload placeholder, no actual file');
      return null;
    }

    // Try to get a signed URL first (works for private buckets)
    const signedUrl = await getSignedUrl(RESUMES_BUCKET, resumeUrl, 3600);
    if (signedUrl) {
      console.log('getResumeUrl: Got signed URL');
      return signedUrl;
    }

    // Fall back to public URL if signed URL fails
    console.log('getResumeUrl: Signed URL failed, trying public URL');
    const { data } = supabase.storage
      .from(RESUMES_BUCKET)
      .getPublicUrl(resumeUrl);

    if (data?.publicUrl) {
      console.log('getResumeUrl: Got public URL');
      return data.publicUrl;
    }

    console.warn('getResumeUrl: Could not generate URL for path:', resumeUrl);
    return null;
  } catch (error) {
    console.error('Error getting resume URL:', error);
    return null;
  }
};

// Get resume public URL (sync version - for immediate use)
export const getResumePublicUrl = (resumeUrl: string): string | null => {
  try {
    // If it's already a full URL (http/https), return as-is
    if (resumeUrl.startsWith('http://') || resumeUrl.startsWith('https://')) {
      return resumeUrl;
    }

    // If it's a placeholder from bulk upload, return null
    if (resumeUrl.startsWith('bulk-upload/')) {
      return null;
    }

    // Otherwise, get public URL from storage
    const { data } = supabase.storage
      .from(RESUMES_BUCKET)
      .getPublicUrl(resumeUrl);

    return data.publicUrl;
  } catch (error) {
    console.error('Error getting resume public URL:', error);
    return null;
  }
};

// Helper: Validate file type
export const validateFileType = (
  file: File,
  allowedTypes: string[]
): boolean => {
  return allowedTypes.includes(file.type);
};

// Helper: Validate file size
export const validateFileSize = (file: File, maxSizeMB: number): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};
